import React, { useState, useEffect } from "react";
import "./Dashboard.css";

const blockNamesList = [
  "Reserve Police Line",
  "Traffic Police Line",
  "K D Line",
  "Police Office Complex",
  "PS Railbazaar",
  "PS Govindnagar",
  "PS Swaroopnagar",
  "PS Nazirabad",
  "RK Chauki Nazirabad",
  "PS Amrapur",
  "PS Fazalganj",
  "PS Gwaltoli",
  "PS Sisamau",
  "PS Chamanganj",
  "PS Bajriya",
  "PS Anwarganj",
  "PS Raipurva",
  "Chauki Coperganj Rajpurva",
  "PS Bapupurva",
  "PS Juhi",
  "PS Cantt",
  "PS Chakeri",
  "Friend Colony Chakeri",
  "PS Barra",
  "PS Kotwali",
  "PS Feelkhana (Patkapur Chauki)",
  "PS Harvanshmohal",
  "PS Kalyanpur",
  "Kalyanpur Zone Residence",
  "PS Panki",
  "PS Bithoor",
  "PS Nawabganj",
  "PS Ghatampur",
  "PS Chaubepur",
  "PS Shivrajpur",
  "PS Sachedi"
];

const getBlockKey = (name) => {
  const index = blockNamesList.indexOf(name);
  return String.fromCharCode(65 + index);
};

function Dashboard() {
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("All Types");
  const [selectedYear, setSelectedYear] = useState("All Years");
  const [quartersData, setQuartersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get API base URL from environment variables
  const API_BASE_URL = process.env.REACT_APP_API_URL;

  const types = ["Type 1", "Type 2", "Type 3"];
  
  // Helper function to get blocks for each type
const getBlocksForType = () => {
  return blockNamesList;
};
  
  // Helper function to check if quarter is vacant
  const isQuarterVacant = (quarter) => {
    return !quarter.resident || 
           quarter.resident.trim() === '' || 
           quarter.resident.toLowerCase() === 'vacant' ||
           quarter.resident.toLowerCase() === 'n/a';
  };
  
  // Fetch data from backend
  useEffect(() => {
    const fetchQuartersData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/quarters`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setQuartersData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching quarters data:', err);
        setError('Failed to load quarters data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuartersData();
  }, [API_BASE_URL]);

  // Get unique years from the data
  const years = ["All Years", ...new Set(quartersData
    .filter(q => q.allotment_year)
    .map(q => q.allotment_year.toString())
  )].sort((a, b) => {
    if (a === "All Years") return -1;
    if (b === "All Years") return 1;
    return b.localeCompare(a);
  });

  // Filter quarters based on search and filters (including pno_no)
  const filterQuarters = (block) => {
    return quartersData.filter(q => {
      const matchesBlock = q.block === block;
      const matchesSearch = searchTerm === "" || 
        q.quarter_no.toLowerCase().includes(searchTerm.toLowerCase()) || 
        (q.pno_no && q.pno_no.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (!isQuarterVacant(q) && q.resident.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesType = selectedType === "All Types" || q.type === selectedType;
      const matchesYear = selectedYear === "All Years" || 
        (q.allotment_year && q.allotment_year.toString() === selectedYear);
      
      return matchesBlock && matchesSearch && matchesType && matchesYear;
    });
  };

  // Calculate occupancy for a specific block and type
  const getOccupancy = (block, type) => {
    const quarters = quartersData.filter(q => q.block === block && q.type === type);
    if (quarters.length === 0) return { filled: 0, vacant: 0, total: 0 };
    
    const filled = quarters.filter(q => !isQuarterVacant(q)).length;
    const vacant = quarters.length - filled;
    
    return { filled, vacant, total: quarters.length };
  };

  // Get quarters for a specific block and type
  const getQuartersForBlock = (block, type) => {
    return quartersData.filter(q => q.block === block && q.type === type);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">
          <p>Loading quarters data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <h1>🏠 House Management Dashboard</h1>
      <p className="dashboard-subtitle">Police Quarters Allocation System</p>
      
      {/* Search and Filters */}
      <div className="filters-container">
        <input 
          type="text" 
          placeholder="Search by quarter no., PNO no., or name..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select 
          value={selectedType} 
          onChange={(e) => setSelectedType(e.target.value)}
          className="filter-select"
        >
          <option value="All Types">All Types</option>
          {types.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        
        <select 
          value={selectedYear} 
          onChange={(e) => setSelectedYear(e.target.value)}
          className="filter-select"
        >
          {years.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
      </div>

      {/* Types and Blocks Grid */}
      <div className="types-container">
        {types.map((type) => {
          const blocks = getBlocksForType(type);
          
          // Check if this type has any matching quarters when searching
          const hasMatchingQuarters = searchTerm ? 
            blocks.some(block => filterQuarters(block).filter(q => q.type === type).length > 0) : 
            true;
          
          // Hide entire type column if no matches
          if (!hasMatchingQuarters) return null;
          
          return (
            <div key={type} className="type-column">
              <h2>{type}</h2>
              <div className="block-list">
                {blocks.map((blockName) => {
  const blockKey = getBlockKey(blockName);

  const occupancy = getOccupancy(blockKey, type);
  const quarters = filterQuarters(blockKey).filter(q => q.type === type);

  if (occupancy.total === 0 && searchTerm) return null;
  if (searchTerm && quarters.length === 0) return null;

  return (
    <div
      key={blockName}
      className="block-card"
      onClick={() => setSelectedBlock({ type, block: blockKey })}
    >
      <div className="occupancy-indicator">
        {occupancy.filled > 0 && (
          <span className="filled-circle">{occupancy.filled}</span>
        )}
        {occupancy.vacant > 0 && (
          <span className="vacant-circle">{occupancy.vacant}</span>
        )}
      </div>
      {blockName}
    </div>
  );
})}
              </div>
            </div>
          );
        })}
      </div>

      {/* Block List Modal */}
      {selectedBlock && (
        <BlockList 
          type={selectedBlock.type} 
          block={selectedBlock.block} 
          onClose={() => setSelectedBlock(null)}
          quartersData={getQuartersForBlock(selectedBlock.block, selectedBlock.type)}
          isQuarterVacant={isQuarterVacant}
          API_BASE_URL={API_BASE_URL}
        />
      )}
    </div>
  );
}

function BlockList({ type, block, onClose, quartersData, isQuarterVacant, API_BASE_URL }) {
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content blocklist-container" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✖</button>
        <h2>{type} - {blockNamesList[block.charCodeAt(0) - 65]}</h2>

        <div className="quarter-grid">
          {quartersData.length > 0 ? (
            quartersData.map((q) => (
              <div
                key={q.id}
                className="quarter-card"
                onClick={() => setSelectedQuarter(q)}
              >
                <h3>{q.quarter_no}</h3>
                <span className="badge">{q.type}</span>
                {!isQuarterVacant(q) ? (
                  <>
                    <p>{q.resident}</p>
                    <small>Allotted in {q.allotment_year}</small>
                  </>
                ) : (
                  <p className="vacant">Vacant</p>
                )}
              </div>
            ))
          ) : (
            <p className="no-quarters">No quarters in this block</p>
          )}
        </div>

        {selectedQuarter && (
          <QuarterModal
            quarter={selectedQuarter}
            onClose={() => setSelectedQuarter(null)}
            isQuarterVacant={isQuarterVacant}
            API_BASE_URL={API_BASE_URL}
          />
        )}
      </div>
    </div>
  );
}

function QuarterModal({ quarter, onClose, isQuarterVacant, API_BASE_URL }) {
  const [electricityBills, setElectricityBills] = useState([]);
  const [maintenanceBills, setMaintenanceBills] = useState([]);
  const [repairWork, setRepairWork] = useState({ major: [], minor: [], others: [] });
  const [billsLoading, setBillsLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch bills data when quarter is selected
  useEffect(() => {
    const fetchBillsData = async () => {
      if (isQuarterVacant(quarter)) return;
      
      try {
        setBillsLoading(true);
        
        // Fetch all bills for the quarter
        const billsResponse = await fetch(`${API_BASE_URL}/api/bills/${quarter.id}`);
        if (billsResponse.ok) {
          const allBills = await billsResponse.json();
          
          // Separate electricity and maintenance bills
          const electricityData = allBills.filter(bill => bill.bill_type === 'electricity');
          const maintenanceData = allBills.filter(bill => bill.bill_type === 'maintenance');
          
          setElectricityBills(electricityData);
          setMaintenanceBills(maintenanceData);
        }
        
        // Fetch repair work data
        const repairResponse = await fetch(`${API_BASE_URL}/api/repairs/${quarter.id}`);
        if (repairResponse.ok) {
          const repairData = await repairResponse.json();
          
          // Debug: Log repair data to see structure
          console.log('Repair data received:', repairData);
          if (repairData.length > 0) {
            console.log('First repair item:', repairData[0]);
            console.log('Image field:', repairData[0].image || repairData[0].images);
          }
          
          // Process repair data into categories
          const processedRepairs = {
            major: repairData.filter(repair => repair.category === 'major'),
            minor: repairData.filter(repair => repair.category === 'minor'),
            others: repairData.filter(repair => repair.category === 'other')
          };
          
          setRepairWork(processedRepairs);
        } else {
          setRepairWork({ major: [], minor: [], others: [] });
        }
        
      } catch (err) {
        console.error('Error fetching bills data:', err);
        setElectricityBills([]);
        setMaintenanceBills([]);
        setRepairWork({ major: [], minor: [], others: [] });
      } finally {
        setBillsLoading(false);
      }
    };

    fetchBillsData();
  }, [quarter.id, quarter.resident, API_BASE_URL, isQuarterVacant]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Helper function to render repair items with images
  const renderRepairItems = (repairs) => {
    if (!repairs || repairs.length === 0) {
      return <span className="no-repairs">Nil</span>;
    }
    
    return (
      <div className="repair-items">
        {repairs.map((repair, index) => {
          // Construct image URL - handle different possible field names
          const imageUrl = repair.image || repair.images || repair.image_url;
          const fullImageUrl = imageUrl ? `${API_BASE_URL}${imageUrl}` : null;
          
          console.log('Repair item:', repair.description);
          console.log('Image URL:', imageUrl);
          console.log('Full Image URL:', fullImageUrl);
          
          return (
            <div key={repair.id || index} className={`repair-detail ${repair.status}`}>
              <p className="repair-description">{repair.description}</p>
              
              {/* Display repair image if available */}
              {fullImageUrl && (
                <div className="repair-image-container" onClick={() => setSelectedImage(fullImageUrl)}>
                  <img 
                    src={fullImageUrl}
                    alt="Repair"
                    className="repair-image"
                    onError={(e) => {
                      console.error('Image failed to load:', fullImageUrl);
                      e.target.parentElement.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', fullImageUrl);
                    }}
                  />
                  <span className="image-label">Click to enlarge</span>
                </div>
              )}
              
              {!fullImageUrl && (
                <div className="no-image-placeholder">
                  <span>No image available</span>
                </div>
              )}
              
              <div className="repair-meta">
                <span className={`status-badge ${repair.status}`}>{repair.status}</span>
                <span className="repair-date">
                  {formatDate(repair.created_at)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const isVacant = isQuarterVacant(quarter);

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content quarter-modal" onClick={(e) => e.stopPropagation()}>
          <button className="close-btn" onClick={onClose}>✖</button>
          
          <h2>Quarter {quarter.quarter_no} Details</h2>
          
          <div className="details-grid">
            <div className="detail-item">
              <label>Quarter Number:</label>
              <span>{quarter.quarter_no}</span>
            </div>
            
            <div className="detail-item">
              <label>Rank:</label>
              <span>{quarter.rank || "N/A"}</span>
            </div>
            
            <div className="detail-item">
              <label>Resident Name:</label>
              <span>{isVacant ? "VACANT" : quarter.resident}</span>
            </div>
            
            {!isVacant && (
              <>
                <div className="detail-item">
                  <label>PNO No:</label>
                  <span>{quarter.pno_no || "N/A"}</span>
                </div>
                
                <div className="detail-item">
                  <label>Mobile No:</label>
                  <span>{quarter.mobile_no || "Not Updated"}</span>
                </div>
                
                <div className="detail-item">
                  <label>Allotment Date & Year:</label>
                  <span>{formatDate(quarter.allotment_date)} ({quarter.allotment_year || "N/A"})</span>
                </div>
              </>
            )}
            
            <div className="detail-item">
              <label>Quarter Type:</label>
              <span>{quarter.type}</span>
            </div>
          </div>

          <div className="section-divider"></div>

          {!isVacant ? (
            <>
              {billsLoading ? (
                <div className="loading-bills">
                  <p>Loading bills data...</p>
                </div>
              ) : (
                <>
                  <h3>Electricity Bill - Monthly Balance</h3>
                  <div className="bills-grid">
                    {electricityBills.length > 0 ? (
                      electricityBills.map((bill, index) => (
                        <div key={index} className={`bill-card ${bill.status}`}>
                          <p>{bill.period}</p>
                          <h4>₹{bill.amount.toLocaleString()}</h4>
                          <span className={`status-badge ${bill.status}`}>{bill.status}</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No electricity bills found</p>
                    )}
                  </div>

                  <div className="section-divider"></div>

                  <h3>Maintenance Bill - Monthly Balance</h3>
                  <div className="bills-grid">
                    {maintenanceBills.length > 0 ? (
                      maintenanceBills.map((bill, index) => (
                        <div key={index} className={`bill-card ${bill.status}`}>
                          <p>{bill.period}</p>
                          <h4>₹{bill.amount.toLocaleString()}</h4>
                          <span className={`status-badge ${bill.status}`}>{bill.status}</span>
                        </div>
                      ))
                    ) : (
                      <p className="no-data">No maintenance bills found</p>
                    )}
                  </div>

                  <div className="section-divider"></div>

                  <h3>Repair Work</h3>
                  <div className="repair-work-section">
                    <div className="repair-category">
                      <h4>Major Repairs</h4>
                      {renderRepairItems(repairWork.major)}
                    </div>
                    
                    <div className="repair-category">
                      <h4>Minor Repairs</h4>
                      {renderRepairItems(repairWork.minor)}
                    </div>
                    
                    <div className="repair-category">
                      <h4>Other Repairs</h4>
                      {renderRepairItems(repairWork.others)}
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="vacant-quarter-info">
              <h3>Quarter Status: VACANT</h3>
              <p>This quarter is currently not allocated to any resident.</p>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal for full-size view */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setSelectedImage(null)}>✖</button>
            <img src={selectedImage} alt="Repair Full View" className="full-size-image" />
          </div>
        </div>
      )}
    </>
  );
}

export default Dashboard;
